import React, { createRef } from 'react';
import PropTypes from 'prop-types';
import { Card, ProgressBar, OverlayTrigger, Tooltip } from 'react-bootstrap-5';
import { connect } from 'react-redux';
import Dropzone from 'react-dropzone';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faImage, faUpload, faFolderPlus } from '@fortawesome/pro-solid-svg-icons';

import {
  createFolder,
  deleteDocument,
  deleteFolder,
  fetchFolder,
  moveDocumentToFolder,
  moveFolderToFolder,
  toggleMarketing,
  uploadDocument,
  updateDocument,
  updateFolder
} from 'components/documents/actions';

import { showTempAlert } from 'bundles/project/actions/index';

import { createDocument, addDocumentToListingMedia } from 'components/documents/api';

import CsvDownloadButton from  'components/csv_download_button';
import DocTemplateSelectorModal from './doc_template_selector_modal';
import EmptyState from 'components/project/empty_state';
import DocumentList from './document_manager/document_list';
import LoadingSpinner from 'components/loading_spinner';
import SearchBar from './document_manager/search_bar';
import FileSystem from 'components/file_system';

const docPropTypes = PropTypes.shape({
  contextId: PropTypes.number,
  editPath: PropTypes.string,
  fetching: PropTypes.bool,
  folder: PropTypes.bool,
  id: PropTypes.number,
  metaDataLabel: PropTypes.string,
  name: PropTypes.string,
  showPath: PropTypes.string,
  uploaded: PropTypes.bool
});

export class DocsTab extends React.Component {
  static propTypes = {
    canToggleMarketing: PropTypes.bool,
    createFolder: PropTypes.func.isRequired,
    currentFolder: docPropTypes,
    deleteDocument: PropTypes.func.isRequired,
    deleteFolder: PropTypes.func.isRequired,
    docTemplateOptions: PropTypes.array,
    fetchFolder: PropTypes.func.isRequired,
    highlightedDocumentId: PropTypes.number,
    marketing: PropTypes.bool,
    moveDocumentToFolder: PropTypes.func.isRequired,
    moveFolderToFolder: PropTypes.func.isRequired,
    project: PropTypes.shape({
      id: PropTypes.number.isRequired,
      rootMarketingDocumentFolderId: PropTypes.number,
      rootNonMarketingDocumentFolderId: PropTypes.number
    }).isRequired,
    projectType: PropTypes.string.isRequired,
    showTempAlert: PropTypes.func.isRequired,
    toggleMarketing: PropTypes.func.isRequired,
    updateDocument: PropTypes.func.isRequired,
    updateFolder: PropTypes.func.isRequired,
    uploadDocument: PropTypes.func.isRequired
  }

  state = {
    dragSourceIndex: null,
    dropTargetIndex: null,
    filePreview: null,
    headFolderId: null,
    showDocTemplateModal: false,
    totalFilesSelected: null,
    totalFilesUploaded: null,
    uploading: false
  }

  componentDidMount() {
    this.fetchDocuments();
  }

  rootFolderId = () => {
    const { marketing, project } = this.props;
    const { rootNonMarketingDocumentFolderId, rootMarketingDocumentFolderId } = project;
    return marketing ? rootMarketingDocumentFolderId : rootNonMarketingDocumentFolderId;
  }

  fetchDocuments() {
    this.props.fetchFolder(this.props.project.id, this.rootFolderId());
  }

  dropzoneRef = createRef()

  openDialog = () => {
    // Note that the ref is set async,
    // so it might be null at some point
    if (this.dropzoneRef.current) this.dropzoneRef.current.open();
  }

  folder = () => {
    return this.props.currentFolder;
  }

  fetching = () => {
    const { currentFolder } = this.props;

    if (currentFolder) return currentFolder.fetching;
    return true;
  }

  documents = () => this.folder().children

  handleFolderSelect = (folderId, highlightDocumentId) => {
    this.props.fetchFolder(this.props.project.id, folderId, highlightDocumentId);
  }

  handleBeginDrag = (dragSourceIndex) => {
    this.setState({ dragSourceIndex });
  }

  handleReorder = (_dragSourceIndex, dropTargetIndex) => {
    this.setState({ dropTargetIndex });
  }

  handleEndDrag = () => {
    const documents = this.documents();
    const source = documents[this.state.dragSourceIndex];
    const target = documents[this.state.dropTargetIndex];

    if (source && target && source !== target && target.folder) {
      if (source.folder) {
        this.props.moveFolderToFolder(this.props.marketing, source.id, target.id).then(this.resetDrag);
      } else {
        this.props.moveDocumentToFolder(this.props.marketing, source.id, source.contextId, target.id)
          .then(this.resetDrag);
      }
    } else {
      this.resetDrag();
    }
  }

  resetDrag = () => {
    this.setState({ dragSourceIndex: null, dropTargetIndex: null });
  }

  moveToParentFolder = (document) => {
    if (document.rootDocument) return;

    const targetId = document.folderParentId;

    if (document.folder) {
      this.props.moveFolderToFolder(this.props.marketing, document.id, targetId);
    } else {
      this.props.moveDocumentToFolder(this.props.marketing, document.id, document.contextId, targetId);
    }
  }

  closeDocTemplateModal = () => {
    this.setState({ showDocTemplateModal: false });
  }

  openDocTemplateModal = (e) => {
    e.preventDefault();
    this.setState({ showDocTemplateModal: true });
  }

  handleFilePreview = (file) => {
    this.setState({ filePreview: file });
  }

  handleExitPreview = () => {
    this.setState({ filePreview: null });
  }

  handleDrop = (files) => {
    if (files.length === 0) return;

    this.setState({ uploading: true, totalFilesSelected: files.length, totalFilesUploaded: 0, headFolderId: null });

    this.uploadFirstFile(files);
    this.setState({ headFolderId: null });
  }

  uploadFirstFile = (files) => {
    if (files.length < 1) return;

    const file = files.pop();
    const { marketing, project, projectType, uploadDocument } = this.props;
    const folder = this.folder();
    const folderId = folder ? folder.id : null;

    uploadDocument(project.id, projectType, marketing, folderId, file, this.state.headFolderId).then((data) => {
      this.setState((prevState) => {
        const totalFilesUploaded = prevState.totalFilesUploaded + 1;
        const nextState = { uploading: totalFilesUploaded !== prevState.totalFilesSelected };

        if (data.headFolderId) nextState.headFolderId = data.headFolderId;

        if (nextState.uploading) return { ...nextState, totalFilesUploaded };

        return { ...nextState, totalFilesUploaded: null, totalFilesSelected: null };
      });

      this.uploadFirstFile(files);
    }).catch(() => {});
  }

  handleAddToListingMedia = (documentId) => {
    addDocumentToListingMedia(this.props.project.id, documentId)
      .then(() => this.addToListingSuccessHandler(documentId))
      .catch(() => this.props.showTempAlert('Something went wrong', true));
  }

  addToListingSuccessHandler = () => {
    this.props.showTempAlert('Successfully added to Listing Media', false);

  }

  createNewDocument = (templateId) => {
    const { project, projectType, marketing } = this.props;

    createDocument(project.id, projectType, marketing, { fromDocumentTemplateId: templateId }).then((response) => {
      this.setState({ showDocTemplateModal: false });
      window.open(response.redirect, '_self');
    });
  }

  createNewFolder = () => {
    this.props.createFolder(this.props.project.id, this.folder().id, this.props.marketing);
  }

  renderSearchBar() {
    return (
      <SearchBar
        marketing={!!this.props.marketing}
        projectId={this.props.project.id}
        onFolderSelect={this.handleFolderSelect}
      />
    );
  }

  renderNonMarketingHeader() {
    const fetching = this.fetching();

    return (
      <React.Fragment>
        <div className="d-flex align-items-center gap-3">
          {!fetching && this.renderZipDownloadButton()}
          {!fetching && this.renderNewFolderButton()}
          <div>{this.renderSearchBar()}</div>
        </div>
      </React.Fragment>
    );
  }

  renderMarketingHeader() {
    const fetching = this.fetching();
    const tooltip = <Tooltip id="upload-icon">Upload File</Tooltip>;

    return (
      <div className="header row-eq-height my-2">
        {!fetching && this.props.docTemplateOptions && (
          <a
            className="btn btn-create"
            disabled={this.state.showDocTemplateModal}
            name="createDocumentLink"
            onClick={this.openDocTemplateModal}
          >
            New Document
          </a>
        )}
        {!fetching && (
          <OverlayTrigger overlay={tooltip} placement="top">
            <div className="text-muted clickable mx-3" onClick={this.openDialog}>
              <FontAwesomeIcon icon={faUpload} size="2x" />
            </div>
          </OverlayTrigger>
        )}
        {!fetching && this.renderNewFolderButton()}
        <div className="search-bar">
          {this.renderSearchBar()}
        </div>
      </div>
    );
  }

  renderHeader() {
    return this.props.marketing ? this.renderMarketingHeader() : this.renderNonMarketingHeader();
  }

  renderNewFolderButton() {
    const tooltip = <Tooltip id='csv-tooltip'>New Folder</Tooltip>;
    return (
      <OverlayTrigger overlay={tooltip} placement="top">
        <div className="clickable text-muted mr-4" name="newFolder" onClick={this.createNewFolder}>
          <FontAwesomeIcon icon={faFolderPlus} size="2x"/>
        </div>
      </OverlayTrigger>
    );
  }

  renderZipDownloadButton() {
    return (
      <CsvDownloadButton
        entityName="attachments"
        fileType="Zip"
        href={`/projects/${this.props.project.id}/document_folders/${this.props.currentFolder.id}.zip`}
      />
    );
  }

  renderFilePreview() {
    return (
      <Card>
        <Card.Body>
          <FileSystem.Preview file={this.state.filePreview} onExit={this.handleExitPreview} />
        </Card.Body>
      </Card>
    );
  }

  render() {
    if (this.fetching()) return <LoadingSpinner />;
    if (this.state.filePreview) return this.renderFilePreview();

    return (
      <React.Fragment>
        {this.renderDocumentList()}
        {this.renderDropZone()}
        <DocTemplateSelectorModal
          docTemplateOptions={this.props.docTemplateOptions}
          show={this.state.showDocTemplateModal}
          onHide={this.closeDocTemplateModal}
          onSubmit={this.createNewDocument}
        />
      </React.Fragment>
    );
  }

  renderDropZone() {
    return (
      <div className="my-4">
        <Dropzone ref={this.dropzoneRef} onDrop={this.handleDrop}>
          {({ getRootProps, getInputProps }) => (
            <div className="media-uploader" {...getRootProps()}>
              {this.state.uploading ? this.renderProgressBar() : this.renderDropBox()}
              <input {...getInputProps()} />
            </div>
          )}
        </Dropzone>
      </div>
    );
  }

  renderDropBox = () => {
    const sub = 'Click "New Document" to create your first Buildout document. Or upload your own by dropping them here';

    return (
      <div className="uploader" name="uploader">
        <div className="text-center text-muted dz-message">
          {this.props.marketing && this.props.currentFolder.children.length < 1 ? (
            <EmptyState
              imageSrc="/images/project_empty_states/documents.svg"
              message="You don't have any documents yet!"
              subMessage={sub}
            />
          ) : (
            <span className="text-muted">
              <FontAwesomeIcon className="me-2 fa-fw" icon={faImage} size="lg" />
              Click or drag files or folders here to upload them.
            </span>
          )}
        </div>
      </div>
    );
  }

  renderProgressBar() {
    return <ProgressBar active now={this.state.totalFilesUploaded / this.state.totalFilesSelected * 100} />;
  }

  renderDocumentList() {
    return (
      <DocumentList
        currentFolder={this.props.currentFolder}
        dragSourceIndex={this.state.dragSourceIndex}
        dropTargetIndex={this.state.dropTargetIndex}
        highlightedDocumentId={this.props.highlightedDocumentId}
        onAddToListingMedia={this.props.projectType === 'Property' ? this.handleAddToListingMedia : null}
        onBeginDrag={this.handleBeginDrag}
        onDeleteDocument={this.props.deleteDocument}
        onDeleteFolder={this.props.deleteFolder}
        onEndDrag={this.handleEndDrag}
        onFilePreview={this.handleFilePreview}
        onFolderSelect={this.handleFolderSelect}
        onMoveToParent={this.moveToParentFolder}
        onReorder={this.handleReorder}
        onToggleMarketing={this.props.canToggleMarketing ? this.props.toggleMarketing : null}
        onUpdateDocument={this.props.updateDocument}
        onUpdateFolder={this.props.updateFolder}
      >
        {this.renderHeader()}
      </DocumentList>
    );
  }
}

const mapStateToProps = ({ project, docs }, ownProps) => ({
  currentFolder: ownProps.marketing ? docs.currentMarketingFolder : docs.currentNonMarketingFolder,
  highlightedDocumentId: docs.selectedDoc,
  project: project,
  projectType: project.className,
  canToggleMarketing: project.className === 'Property' || project.canViewUnreleasedFeatures
});

const mapDispatchToProps = {
  createFolder,
  deleteDocument,
  deleteFolder,
  fetchFolder,
  moveDocumentToFolder,
  moveFolderToFolder,
  showTempAlert,
  toggleMarketing,
  updateDocument,
  updateFolder,
  uploadDocument
};

export default connect(mapStateToProps, mapDispatchToProps)(DocsTab);
